import React, { useCallback, useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Star, ExternalLink, ChevronDown } from 'lucide-react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { useModuleConfig } from './ModuleConfigContext'
import ReviewContent from './ReviewContent'
import { displayRolePlayTitle } from './rolePlayStatus'

const GET_REVIEWS = gql`
  query GetReviews($limit: Int, $offset: Int) {
    getReviews(limit: $limit, offset: $offset) {
      _id
      rolePlayName
      rolePlayId
      score
      mode
      feedback
      mastered
      toImprove
      sessionToken
      createdAt
    }
  }
`

// Star Rating Component
const StarRating: React.FC<{ rating: number; maxRating?: number }> = ({ rating, maxRating = 5 }) => {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: maxRating }, (_, index) => (
        <Star key={index} size={16} className={index < rating ? 'fill-[#3D8C71] text-[#3D8C71]' : 'fill-none text-gray-300'} />
      ))}
    </div>
  )
}

interface ReviewEntry {
  _id: string
  rolePlayName: string
  rolePlayId: number
  score: number
  mode: string
  feedback: string
  mastered: string | null
  toImprove: string | null
  sessionToken: string | null
  createdAt: string
}

const formatDate = (isoString: string): string => {
  const date = new Date(isoString)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

const Module2Review: React.FC = () => {
  const moduleConfig = useModuleConfig()
  const pageSize = moduleConfig.reviewPageSize
  const [reviews, setReviews] = useState<ReviewEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchReviews = useCallback(
    async (offset: number) => {
      const response = await authorizedGraphQLRequest<{ getReviews: ReviewEntry[] }>(GET_REVIEWS, {
        limit: pageSize,
        offset,
      })
      return response.getReviews || []
    },
    [pageSize],
  )

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const data = await fetchReviews(0)
        setReviews(data)
        setHasMore(data.length === pageSize)
      } catch (err) {
        console.error('Failed to fetch reviews:', err)
      } finally {
        setIsLoading(false)
      }
    }
    loadInitial()
  }, [fetchReviews])

  const handleShowMore = async () => {
    setIsLoadingMore(true)
    try {
      const data = await fetchReviews(reviews.length)
      setReviews((prev) => [...prev, ...data])
      setHasMore(data.length === pageSize)
    } catch (err) {
      console.error('Failed to fetch more reviews:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleLinkClick = (sessionToken: string | null) => {
    if (sessionToken) {
      window.location.href = `/chat/${sessionToken}`
    }
  }

  return (
    <div className="bg-card text-card-foreground rounded-xl flex flex-col h-full md:flex-1 md:max-h-full min-h-0 border-0 shadow-xl overflow-hidden">
      {/* Header Section */}
      <div className="text-center pt-6 pb-4 px-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-primary tracking-tight">Review</h1>
        <p className="text-gray-400 text-sm mt-2">Your role play history and performance</p>
      </div>

      <Card className="p-6 border-0 flex-1 min-h-0 overflow-y-auto">
        <CardContent className="flex-1 p-0 rounded-lg border border-gray-200">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-500 mt-4">Loading reviews...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <p>No review history available</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 p-4 md:p-6 bg-gradient-to-b from-gray-50 to-white">
                {reviews.map((entry, index) => {
                  const isExpanded = expandedId === entry._id
                  return (
                    <div
                      key={entry._id}
                      className={`group rounded-2xl border bg-white/70 backdrop-blur-sm shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden ${
                        index % 2 === 0 ? 'border-gray-200' : 'border-primary/10'
                      }`}
                    >
                      {/* Основной блок карточки — одна строка на десктопе */}
                      <div className="flex flex-col gap-3 px-4 py-3 md:px-6 md:py-4 md:flex-row md:items-center md:gap-4 md:flex-nowrap">
                        {/* Role Play Name */}
                        <div className="min-w-0 md:w-[22%]">
                          <p className="text-[11px] uppercase tracking-[0.14em] text-gray-400 font-semibold mb-1">Role Play Name</p>
                          <p className="text-sm md:text-[15px] font-semibold text-gray-900 truncate" title={displayRolePlayTitle(entry.rolePlayName)}>
                            {displayRolePlayTitle(entry.rolePlayName)}
                          </p>
                        </div>

                        {/* Date, Score, Mode */}
                        <div className="flex flex-wrap items-center gap-2 md:gap-2.5 md:w-[30%]">
                          <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 border border-gray-100">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-semibold">Date</span>
                            <span className="text-xs font-medium text-gray-700">{formatDate(entry.createdAt)}</span>
                          </div>

                          <div className="inline-flex items-center gap-1.5 px-3 py-1">
                            <StarRating rating={entry.score} />
                          </div>

                          {/* <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] font-medium text-primary">
                            {entry.mode}
                          </span> */}
                        </div>

                        {/* Feedback preview */}
                        <div className="min-w-0 md:flex-1">
                          <p className="text-[11px] uppercase tracking-[0.16em] text-gray-400 font-semibold mb-1">Feedback</p>
                          <p className="text-xs text-gray-600 line-clamp-2 md:line-clamp-1" title={entry.feedback}>
                            {truncate(entry.feedback, 140)}
                          </p>
                        </div>

                        {/* History + Toggle */}
                        <div className="flex items-center justify-between gap-2 md:gap-3 md:justify-end md:w-[20%]">
                          {entry.sessionToken ? (
                            <button
                              onClick={() => handleLinkClick(entry.sessionToken)}
                              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-700 hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
                            >
                              <span>History</span>
                              <ExternalLink size={14} className="text-gray-400 group-hover:text-primary transition-colors" />
                            </button>
                          ) : (
                            <span className="text-gray-300 text-[11px]">No history</span>
                          )}

                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : entry._id)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white text-[11px] font-medium px-3.5 py-1.5 shadow-sm hover:shadow-md hover:bg-primary/90 transition-all"
                          >
                            <span>{isExpanded ? 'Hide' : 'Details'}</span>
                            <span
                              className={`h-4 w-4 inline-flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-[10px] transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            >
                              <ChevronDown size={12} />
                            </span>
                          </button>
                        </div>
                      </div>

                      {/* Аккордеон с полными деталями ревью */}
                      <div
                        className={`border-t bg-gradient-to-b from-primary/3 to-primary/5 transition-all duration-300 ease-out ${
                          isExpanded ? 'max-h-[34rem] md:max-h-[38rem] opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className={`px-4 md:px-6 ${isExpanded ? 'py-4 md:py-5' : 'py-0'}`}>
                          <ReviewContent mode={entry.mode} score={entry.score} createdAt={entry.createdAt} mastered={entry.mastered} toImprove={entry.toImprove} feedback={entry.feedback} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Show More Button */}
              {hasMore && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={handleShowMore}
                    disabled={isLoadingMore}
                    className="px-6 py-2 text-sm font-medium text-primary hover:text-primary/80 border border-primary/30 hover:border-primary/50 rounded-full transition-all disabled:opacity-50"
                  >
                    {isLoadingMore ? 'Loading...' : 'Show more'}
                  </button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Module2Review
